import React, { useRef } from "react";
import { Button, Spinner } from "components/ui/BootstrapCompat";
import { FiCheck, FiChevronUp, FiImage, FiTrash2, FiUploadCloud } from "react-icons/fi";
import { ALERT_IMAGE_ACCEPT, DEFAULT_WIDGET_IMAGE } from "./constants";

export function Panel({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-[#dbe4ef] bg-white p-4 shadow-[0_8px_24px_rgba(16,24,40,0.04)]">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="grid h-7 w-7 flex-none place-items-center rounded-lg bg-[#f4f7fb] text-[#667085]">
            {icon}
          </span>
          <h2 className="m-0 truncate text-sm font-black text-[#101828]">
            {title}
          </h2>
        </div>
        <FiChevronUp className="h-4 w-4 flex-none text-[#98a2b3]" />
      </div>
      {children}
    </section>
  );
}

export function AlertImageUploadControl({
  label,
  urlLabel,
  value,
  inputId,
  isUploading,
  progress,
  error,
  onUpload,
  onUrlChange,
  onClear,
}: {
  label: string;
  urlLabel: string;
  value: string;
  inputId: string;
  isUploading: boolean;
  progress: number;
  error: string;
  onUpload: (file: File) => void;
  onUrlChange: (value: string) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const previewUrl = value || DEFAULT_WIDGET_IMAGE;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) onUpload(file);
    event.target.value = "";
  };

  return (
    <div>
      <div className="mb-1 text-xs font-bold text-[#667085]">{label}</div>
      <div className="grid grid-cols-1 gap-3 rounded-lg border border-[#dbe4ef] bg-white p-3 sm:grid-cols-[88px_1fr]">
        <button
          type="button"
          className="relative grid h-[88px] w-[88px] place-items-center overflow-hidden rounded-lg border border-[#dbe4ef] bg-[#f8fafc] text-[#98a2b3] transition hover:border-[#168feb]"
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
          title="Upload image"
        >
          {value ? (
            <img
              src={previewUrl}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <FiImage className="h-7 w-7" />
          )}
          {isUploading && (
            <span className="absolute inset-0 grid place-items-center bg-white/80">
              <Spinner animation="border" size="sm" />
            </span>
          )}
        </button>

        <div className="min-w-0">
          <TextInput label={urlLabel} value={value} onChange={onUrlChange} />
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              variant="outline-primary"
              className="inline-flex h-9 items-center gap-2 rounded-lg px-3 text-xs font-bold"
              disabled={isUploading}
              onClick={() => inputRef.current?.click()}
            >
              {isUploading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FiUploadCloud className="h-4 w-4" />
              )}
              ອັບໂຫຼດ
            </Button>
            <Button
              variant="light"
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-[#dbe4ef] bg-white px-3 text-xs font-bold text-[#344054]"
              disabled={isUploading || !value}
              onClick={onClear}
            >
              <FiTrash2 className="h-4 w-4" />
              ລ້າງ
            </Button>
          </div>
          {isUploading && (
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#edf3fa]">
              <div
                className="h-full rounded-full bg-[#168feb] transition-all"
                style={{ width: `${Math.max(progress, 1)}%` }}
              />
            </div>
          )}
          {error && (
            <div className="mt-2 text-xs font-bold text-[#b42318]">{error}</div>
          )}
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept={ALERT_IMAGE_ACCEPT}
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </div>
    </div>
  );
}

export function TextInput({
  label,
  value,
  onChange,
  type = "text",
  note,
  suffix,
  readOnly,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  note?: string;
  suffix?: string;
  readOnly?: boolean;
}) {
  return (
    <label className="block min-w-0 flex-1">
      <span className="mb-1 block text-xs font-bold text-[#667085]">
        {label}
      </span>
      <span className="flex h-10 items-center rounded-lg border border-[#dbe4ef] bg-[#f8fafc] focus-within:border-[#168feb] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#d9efff]">
        <input
          type={type}
          readOnly={readOnly}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-full min-w-0 flex-1 rounded-lg border-0 bg-transparent px-3 text-sm font-bold text-[#344054] outline-none"
        />
        {suffix && (
          <span className="flex h-full items-center border-l border-[#dbe4ef] px-3 text-xs font-black text-[#475467]">
            {suffix}
          </span>
        )}
      </span>
      {note && (
        <span className="mt-1 block text-[11px] font-medium text-[#667085]">
          {note}
        </span>
      )}
    </label>
  );
}

export function SelectInput({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-xs font-bold text-[#667085]">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-lg border border-[#dbe4ef] bg-[#f8fafc] px-3 text-sm font-bold text-[#344054] outline-none transition focus:border-[#168feb] focus:bg-white focus:ring-2 focus:ring-[#d9efff]"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ColorInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-xs font-bold text-[#667085]">
        {label}
      </span>
      <span className="flex h-10 items-center gap-2 rounded-lg border border-[#dbe4ef] bg-[#f8fafc] px-3">
        <input
          type="color"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-5 w-8 cursor-pointer border-0 bg-transparent p-0"
        />
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="min-w-0 flex-1 border-0 bg-transparent text-sm font-black text-[#344054] outline-none"
        />
      </span>
    </label>
  );
}

export function RangeInput({
  label,
  value,
  min,
  max,
  onChange,
  className,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  className?: string;
}) {
  return (
    <label className={`block ${className || ""}`}>
      <span className="mb-1 flex items-center justify-between text-xs font-bold text-[#667085]">
        <span>{label}</span>
        <span>{value}%</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className="h-2 w-full accent-[#168feb]"
      />
    </label>
  );
}

export function ToggleRow({
  label,
  description,
  checked,
  onChange,
  className,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
}) {
  return (
    <div
      className={`flex min-h-[58px] items-center gap-3 rounded-lg border border-[#dbe4ef] bg-[#f8fafc] px-3 ${className || ""}`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-black text-[#344054]">{label}</div>
        <div className="truncate text-xs font-medium text-[#667085]">
          {description}
        </div>
      </div>
      <ToggleSwitch checked={checked} onChange={onChange} />
    </div>
  );
}

export function ToggleSwitch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      className={`relative h-6 w-11 flex-none rounded-full transition ${
        checked ? "bg-[#168feb]" : "bg-[#e6ebf1]"
      }`}
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
    >
      <span
        className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition ${
          checked ? "left-6" : "left-1"
        }`}
      />
    </button>
  );
}

export function SegmentedControl({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  className?: string;
}) {
  return (
    <div className={className}>
      <span className="mb-1 block text-xs font-bold text-[#667085]">
        {label}
      </span>
      <div className="grid grid-cols-2 rounded-lg bg-[#edf3fa] p-1">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`h-9 rounded-md text-xs font-black transition ${
              value === option.value
                ? "bg-white text-[#168feb] shadow-sm"
                : "text-[#667085] hover:bg-white/70"
            }`}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
